import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'
import {
  isCreationCanvasPath,
  isCreativeDirectorPath,
  isProductManagePath,
  isProductMaterialPagePath,
  isPublishTaskPagePath,
  isTikTokBulkPublishPath,
} from '@/common/utils/menu-route'
import { isGenerationRecordsPagePath } from '@/common/utils/generation-route'

export interface TagView extends Partial<RouteLocationNormalized> {
  title?: string
  affix?: boolean
}

/** 规范化路径：去掉末尾斜杠，避免 /product 与 /product/ 被视为不同导致重复标签 */
const normalizePath = (p: string | undefined) => (p || '').replace(/\/+$/, '') || '/'

/** 商品管理页在标签里统一使用的 path */
const PRODUCT_MANAGE_UNIFIED_PATH = '/product'

export const useTagsViewStore = defineStore('tagsView', () => {
  // State
  const visitedViews = ref<TagView[]>([])
  const cachedViews = ref<string[]>([])

  // Actions
  const addView = (view: TagView) => {
    // 对于视频生成页面，需要同时检查路径和商品ID，确保不同商品创建不同的标签页
    if (view.path === '/video-generation') {
      const currentProductId = view.query?.productId
      if (currentProductId && visitedViews.value.some(v => 
        v.path === view.path && v.query?.productId === currentProductId
      )) {
        return // 相同商品ID的视频生成页面已存在，不重复添加
      }
    } else if (isProductManagePath(view.path)) {
      // 商品管理：无论从 /product 还是 /sys/goods-manage 进入，只保留一个标签，统一为 /product
      visitedViews.value = visitedViews.value.filter(
        v => v.affix || !isProductManagePath(v.path)
      )
    } else {
      // 其他页面：同一路径始终只保留一个标签（含固定标签）。先移除同路径的非固定标签，若已存在同路径标签则不再添加，避免重复“首页”等
      const normalizedViewPath = normalizePath(view.path)
      visitedViews.value = visitedViews.value.filter(
        v => v.affix || normalizePath(v.path) !== normalizedViewPath
      )
      if (visitedViews.value.some(v => normalizePath(v.path) === normalizedViewPath)) {
        return
      }
    }
    
    // 限制最多15个标签页（排除固定标签）
    const MAX_TAGS = 15
    
    // 在添加前检查：如果添加后会超过限制，先删除最旧的标签页
    // 循环删除，直到添加后不超过15个
    while (true) {
      const nonAffixViews = visitedViews.value.filter(v => !v.affix)
      
      // 如果当前非固定标签数量已经小于限制，可以添加新标签
      if (nonAffixViews.length < MAX_TAGS) {
        break
      }
      
      // 找到最早打开的非固定标签页（FIFO：先进先出）
      // 由于 visitedViews 是按添加顺序排列的，第一个非固定标签就是最早打开的
      const oldestNonAffixView = visitedViews.value.find(v => !v.affix)
      
      if (oldestNonAffixView) {
        // 删除最早打开的非固定标签页
        delView(oldestNonAffixView)
        delCachedView(oldestNonAffixView)
        console.log(`[标签页管理] 标签页数量将达到${MAX_TAGS}个上限，自动关闭最早打开的标签页: ${oldestNonAffixView.title}`)
      } else {
        // 如果没有找到非固定标签页，跳出循环（理论上不应该发生）
        break
      }
    }
    
    // 获取原始标题（保持中文/稳定 key，显示时再按当前语言翻译）
    let rawTitle = (view.meta?.title as string) || (typeof view.name === 'string' ? view.name : '未命名')
    
    // 对于视频生成页面，如果有商品ID，尝试获取商品名称作为标题的一部分
    if (view.path === '/video-generation' && view.query?.productId) {
      const productName = (view.meta as any)?.productName
      if (productName) {
        // 存稳定 key，不在此处拼当前语言文案
        rawTitle = '视频生成'
        if (!view.meta) view.meta = {}
        view.meta.fullProductTitle = productName
      } else {
        rawTitle = '视频生成'
      }
    }

    // 添加新标签页（商品管理统一存 /product，非视频生成页存规范化 path）
    const pathToStore =
      view.path === '/video-generation'
        ? view.path
        : isProductManagePath(view.path)
          ? PRODUCT_MANAGE_UNIFIED_PATH
          : normalizePath(view.path)
    visitedViews.value.push({
      ...view,
      path: pathToStore,
      title: rawTitle,
      affix: Boolean(view.affix ?? view.meta?.affix ?? false)
    })
  }

  const resolveCacheNames = (view: TagView) => {
    // 商品管理动态路由 name 多为「商品管理」，keep-alive 只认组件名 Product
    if (isProductManagePath(view.path)) return ['Product']
    // 商品素材动态路由 name 多为菜单标题，keep-alive 只认组件名 ProductMaterial
    if (isProductMaterialPagePath(view.path || '')) return ['ProductMaterial']
    // 发布中心动态路由 name 多为菜单标题，keep-alive 只认组件名 PublishTask
    if (isPublishTaskPagePath(view.path || '')) return ['PublishTask']
    // 生成记录动态路由 name 多为菜单标题，keep-alive 只认组件名 GenerationRecords
    if (isGenerationRecordsPagePath(view.path || '')) return ['GenerationRecords']
    // 批量发布页文件名是 index，动态菜单 name 也常是标题；keep-alive 只认组件名 TikTokBulkPublish
    if (isTikTokBulkPublishPath(view.path)) return ['TikTokBulkPublish']
    // 创意编导同上：菜单标题与路由名不一致时也要保住同一份页面
    if (isCreativeDirectorPath(view.path || '')) return ['TikTokCreativeDirector']
    // 创作画布动态路由 name 多为「创作画布」；组件名 CreationCanvas（实际由布局常驻挂载 iframe）
    if (isCreationCanvasPath(view.path || '')) return ['CreationCanvas']
    return typeof view.name === 'string' && view.name ? [view.name] : []
  }

  /** keep-alive 只认这个名字；页面文件名多为 index，必须和标签缓存名单一致 */
  const resolveKeepAliveName = (view: TagView) => resolveCacheNames(view)[0] || ''

  const addCachedView = (view: TagView) => {
    for (const name of resolveCacheNames(view)) {
      if (!cachedViews.value.includes(name)) {
        cachedViews.value.push(name)
      }
    }
  }

  const delView = (view: TagView) => {
    // 对于视频生成页面，需要同时检查路径和商品ID
    if (view.path === '/video-generation') {
      const productId = view.query?.productId
      const index = visitedViews.value.findIndex(v => 
        v.path === view.path && v.query?.productId === productId
      )
      if (index > -1) {
        visitedViews.value.splice(index, 1)
      }
    } else if (isProductManagePath(view.path)) {
      const index = visitedViews.value.findIndex(v => isProductManagePath(v.path))
      if (index > -1) {
        visitedViews.value.splice(index, 1)
      }
    } else {
      // 其他页面按路径判断（规范化后比较）
      const normalizedPath = normalizePath(view.path)
      const index = visitedViews.value.findIndex(v => normalizePath(v.path) === normalizedPath)
      if (index > -1) {
        visitedViews.value.splice(index, 1)
      }
    }
  }

  const delCachedView = (view: TagView) => {
    for (const name of resolveCacheNames(view)) {
      const index = cachedViews.value.indexOf(name)
      if (index > -1) {
        cachedViews.value.splice(index, 1)
      }
    }
  }

  const delOthersViews = (view: TagView) => {
    // 对于视频生成页面，需要同时检查路径和商品ID
    if (view.path === '/video-generation') {
      const productId = view.query?.productId
      visitedViews.value = visitedViews.value.filter(v => {
        return v.affix || (v.path === view.path && v.query?.productId === productId)
      })
    } else if (isProductManagePath(view.path)) {
      // 保留固定标签 + 当前商品管理标签（与视频生成分支一致）
      visitedViews.value = visitedViews.value.filter(v => {
        return v.affix || isProductManagePath(v.path)
      })
    } else {
      // 其他页面按路径判断（规范化后比较）
      const normalizedPath = normalizePath(view.path)
      visitedViews.value = visitedViews.value.filter(v => {
        return v.affix || normalizePath(v.path) === normalizedPath
      })
    }
  }

  const delAllViews = () => {
    visitedViews.value = visitedViews.value.filter(v => v.affix)
    cachedViews.value = []
  }

  // 更新标签页标题
  const updateViewTitle = (path: string, query: any, newTitle: string) => {
    const view = visitedViews.value.find(v => {
      if (path === '/video-generation') {
        // 确保商品ID类型匹配（都转换为字符串比较）
        const viewProductId = String(v.query?.productId || '')
        const queryProductId = String(query?.productId || '')
        return v.path === path && viewProductId === queryProductId
      }
      if (isProductManagePath(path)) {
        return isProductManagePath(v.path)
      }
      return normalizePath(v.path) === normalizePath(path)
    })
    if (view) {
      view.title = newTitle
      console.log('[标签页管理] 已更新标签页标题:', path, query, newTitle)
    } else {
      console.warn('[标签页管理] 未找到要更新的标签页:', path, query)
    }
  }

  return {
    visitedViews,
    cachedViews,
    addView,
    addCachedView,
    resolveKeepAliveName,
    delView,
    delCachedView,
    delOthersViews,
    delAllViews,
    updateViewTitle
  }
})

